/**
 * Navigation — Sticky header, tree icon injection, search overlay.
 *
 * Dropdown menus and mobile overlay are handled by core/navigation.
 * This file enhances with: transparent→solid sticky behavior, the
 * decorative tree icon on dropdown parents, and the search overlay.
 *
 * @package DCLT
 */

( function () {
	'use strict';

	const header        = document.querySelector( '.dclt-site-header' );
	const searchToggle  = document.querySelector( '[data-search-toggle]' );
	const searchOverlay = document.querySelector( '[data-search-overlay]' );
	const searchClose   = document.querySelector( '[data-search-close]' );
	const searchInput   = searchOverlay ? searchOverlay.querySelector( 'input[type="search"]' ) : null;

	if ( ! header ) return;

	const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

	// ═══════════════════════════════════════════════════════
	// Sticky Header
	// ═══════════════════════════════════════════════════════

	const isTransparent = header.classList.contains( 'dclt-site-header--transparent' );
	const isAdminBar    = document.body.classList.contains( 'admin-bar' );
	let ticking = false;

	function updateHeader() {
		const scrollY = window.scrollY;

		if ( isTransparent ) {
			header.classList.toggle( 'dclt-site-header--solid', scrollY > 100 );
		}

		header.classList.toggle( 'dclt-site-header--condensed', scrollY > 300 );

		// On mobile (≤600px) the WP admin bar scrolls with the page.
		// Shrink the offset from 46px → 0 as the admin bar leaves the viewport.
		if ( isAdminBar && window.innerWidth <= 600 ) {
			var offset = Math.max( 0, 46 - scrollY );
			document.documentElement.style.setProperty( '--dclt-mobile-admin-offset', offset + 'px' );
		}

		ticking = false;
	}

	window.addEventListener( 'scroll', function () {
		if ( ! ticking ) {
			window.requestAnimationFrame( updateHeader );
			ticking = true;
		}
	}, { passive: true } );

	updateHeader();

	// ═══════════════════════════════════════════════════════
	// Tree Icon Injection
	// ═══════════════════════════════════════════════════════
	// Inject a decorative tree SVG into each dropdown parent
	// inside the primary nav. The icon is purely visual and
	// animated via CSS (scaleY grow-up on hover).

	const treeSvg = '<span class="dclt-tree-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="28" height="28" fill="currentColor"><path d="M210.6 5.9L62 169.4c-3.9 4.2-6 9.8-6 15.5 0 12.7 10.3 23 23 23h49.4L58.9 290.7c-4.3 4.4-6.9 10.4-6.9 16.8 0 12.7 10.3 23 23 23h41.2L40.2 428.4c-4.8 5-7.2 11.6-7.2 18.4 0 14.1 11.5 25.6 25.6 25.6H192v15c0 13.8 11.2 25 25 25h14c13.8 0 25-11.2 25-25v-15h133.4c14.1 0 25.6-11.5 25.6-25.6 0-6.8-2.5-13.4-7.2-18.4L331.8 330.5H373c12.7 0 23-10.3 23-23 0-6.4-2.6-12.4-6.9-16.8L319.6 207.9H369c12.7 0 23-10.3 23-23 0-5.7-2.1-11.3-6-15.5L238.4 5.9C234.2 1.3 228.3-1 222-1h-4c-6.3 0-12.2 2.3-16.4 6.9h9z"/></svg></span>';

	const primaryNav = document.querySelector( '.dclt-primary-nav' );
	if ( primaryNav ) {
		const dropdownParents = primaryNav.querySelectorAll( '.wp-block-navigation-item.has-child' );
		dropdownParents.forEach( function ( item ) {
			// Avoid duplicates on SPA-style navigations.
			if ( ! item.querySelector( '.dclt-tree-icon' ) ) {
				item.insertAdjacentHTML( 'beforeend', treeSvg );
			}
		} );
	}

	// ═══════════════════════════════════════════════════════
	// Search Overlay
	// ═══════════════════════════════════════════════════════

	// Both overlay max-height and .wp-site-blocks padding-top are driven
	// by --dclt-search-offset. One setProperty call triggers both
	// transitions on the same browser frame.

	var searchInner = searchOverlay ? searchOverlay.querySelector( '.dclt-search-overlay__inner' ) : null;

	function openSearch() {
		if ( ! searchOverlay ) return;

		searchOverlay.removeAttribute( 'hidden' );

		// Force reflow so browser sees max-height: 0 and translateY(-100%)
		// before transitions start.
		void searchOverlay.offsetHeight;

		// Measure the inner content (not scrollHeight which can overcount).
		var height = searchInner ? searchInner.offsetHeight : searchOverlay.scrollHeight;

		// All three changes batched before next paint — transitions start together:
		// 1. max-height: 0 → height (overlay grows)
		// 2. padding-top: 0 → height (page pushes down)
		// 3. translateY: -100% → 0 (inner slides in)
		searchOverlay.classList.add( 'dclt-search-overlay--open' );
		document.documentElement.style.setProperty( '--dclt-search-offset', height + 'px' );

		if ( searchToggle ) searchToggle.setAttribute( 'aria-expanded', 'true' );
		if ( searchInput ) setTimeout( function () { searchInput.focus(); }, 50 );
	}

	function closeSearch() {
		if ( ! searchOverlay ) return;

		// Both changes batched — overlay shrinks, inner slides up, padding resets.
		searchOverlay.classList.remove( 'dclt-search-overlay--open' );
		document.documentElement.style.setProperty( '--dclt-search-offset', '0px' );

		if ( ! prefersReducedMotion.matches ) {
			searchOverlay.addEventListener( 'transitionend', function handler( e ) {
				if ( e.propertyName !== 'max-height' ) return;
				searchOverlay.setAttribute( 'hidden', '' );
				searchOverlay.removeEventListener( 'transitionend', handler );
			} );
		} else {
			searchOverlay.setAttribute( 'hidden', '' );
		}

		if ( searchToggle ) {
			searchToggle.setAttribute( 'aria-expanded', 'false' );
			searchToggle.focus();
		}
	}

	if ( searchToggle ) {
		searchToggle.addEventListener( 'click', function () {
			var isOpen = searchToggle.getAttribute( 'aria-expanded' ) === 'true';
			isOpen ? closeSearch() : openSearch();
		} );
	}

	if ( searchClose ) {
		searchClose.addEventListener( 'click', closeSearch );
	}

	// Focus trap for search overlay.
	if ( searchOverlay ) {
		searchOverlay.addEventListener( 'keydown', function ( e ) {
			if ( e.key === 'Escape' ) {
				closeSearch();
				return;
			}
			if ( e.key !== 'Tab' ) return;

			var focusable = searchOverlay.querySelectorAll( 'input, button, [tabindex]:not([tabindex="-1"])' );
			var first = focusable[ 0 ];
			var last  = focusable[ focusable.length - 1 ];

			if ( e.shiftKey && document.activeElement === first ) {
				e.preventDefault();
				last.focus();
			} else if ( ! e.shiftKey && document.activeElement === last ) {
				e.preventDefault();
				first.focus();
			}
		} );
	}

	// ═══════════════════════════════════════════════════════
	// Mobile Overlay Extras
	// ═══════════════════════════════════════════════════════
	// Inject donate button, secondary nav links, and a search
	// field into the WP navigation mobile overlay so they're
	// available on small screens.

	var overlayContent = document.querySelector( '.dclt-primary-nav .wp-block-navigation__responsive-container-content' );

	if ( overlayContent && ! overlayContent.querySelector( '.dclt-mobile-nav-extras' ) ) {
		var extras = document.createElement( 'div' );
		extras.className = 'dclt-mobile-nav-extras';

		// Donate button.
		var donateLink = document.querySelector( '.dclt-header__donate-btn .wp-block-button__link' );
		if ( donateLink ) {
			var donate = document.createElement( 'a' );
			donate.href = donateLink.getAttribute( 'href' ) || '/give-back/donate/';
			donate.className = 'dclt-mobile-nav-extras__donate';
			donate.textContent = 'DONATE';
			extras.appendChild( donate );
		}

		// Secondary nav links (Contact Us, Calendar).
		var secondaryItems = document.querySelectorAll( '.dclt-secondary-nav .wp-block-navigation-item__content' );
		if ( secondaryItems.length ) {
			var linksWrap = document.createElement( 'div' );
			linksWrap.className = 'dclt-mobile-nav-extras__links';
			secondaryItems.forEach( function ( item ) {
				var a = document.createElement( 'a' );
				a.href = item.getAttribute( 'href' );
				a.className = 'dclt-mobile-nav-extras__link';
				a.textContent = item.textContent.trim();
				linksWrap.appendChild( a );
			} );
			extras.appendChild( linksWrap );
		}

		// Search field.
		var siteUrl = window.location.origin + '/';
		var searchForm = document.createElement( 'form' );
		searchForm.className = 'dclt-mobile-nav-extras__search';
		searchForm.action = siteUrl;
		searchForm.method = 'get';

		var searchFieldDiv = document.createElement( 'div' );
		searchFieldDiv.className = 'dclt-mobile-nav-extras__search-field';

		// Search icon (static SVG via namespace-aware DOM).
		var svgNs = 'http://www.w3.org/2000/svg';
		var svg = document.createElementNS( svgNs, 'svg' );
		svg.setAttribute( 'aria-hidden', 'true' );
		svg.setAttribute( 'focusable', 'false' );
		svg.setAttribute( 'width', '18' );
		svg.setAttribute( 'height', '18' );
		svg.setAttribute( 'viewBox', '0 0 24 24' );
		svg.setAttribute( 'fill', 'none' );
		svg.setAttribute( 'stroke', 'currentColor' );
		svg.setAttribute( 'stroke-width', '2' );
		svg.setAttribute( 'stroke-linecap', 'round' );
		svg.setAttribute( 'stroke-linejoin', 'round' );
		var circle = document.createElementNS( svgNs, 'circle' );
		circle.setAttribute( 'cx', '11' );
		circle.setAttribute( 'cy', '11' );
		circle.setAttribute( 'r', '8' );
		svg.appendChild( circle );
		var line = document.createElementNS( svgNs, 'line' );
		line.setAttribute( 'x1', '21' );
		line.setAttribute( 'y1', '21' );
		line.setAttribute( 'x2', '16.65' );
		line.setAttribute( 'y2', '16.65' );
		svg.appendChild( line );
		searchFieldDiv.appendChild( svg );

		var searchInput2 = document.createElement( 'input' );
		searchInput2.type = 'search';
		searchInput2.name = 's';
		searchInput2.placeholder = 'What are you searching for?';
		searchInput2.autocomplete = 'off';
		searchFieldDiv.appendChild( searchInput2 );

		searchForm.appendChild( searchFieldDiv );
		extras.appendChild( searchForm );

		overlayContent.appendChild( extras );
	}

	// ═══════════════════════════════════════════════════════
	// Mobile Nav: Close Button + SVG Wordmark
	// ═══════════════════════════════════════════════════════
	// The overlay sits below the fixed header. We create a separate
	// close (X) button in the header bar (next to the hamburger) and
	// inject the SVG wordmark for mobile. The hamburger is hidden when
	// the menu is open; the close button is hidden when it's closed.

	var navOverlay = document.querySelector( '.dclt-primary-nav .wp-block-navigation__responsive-container' );
	var openBtn    = document.querySelector( '.dclt-primary-nav .wp-block-navigation__responsive-container-open' );
	var wpCloseBtn = navOverlay ? navOverlay.querySelector( '.wp-block-navigation__responsive-container-close' ) : null;
	var navGroup   = document.querySelector( '.dclt-header__nav-group' );

	if ( navOverlay && openBtn && navGroup ) {
		// Replace default WP hamburger SVG with the Figma design icon.
		var oldHamburger = openBtn.querySelector( 'svg' );
		if ( oldHamburger ) {
			var svgNsH = 'http://www.w3.org/2000/svg';
			var newHamburger = document.createElementNS( svgNsH, 'svg' );
			newHamburger.setAttribute( 'width', '21' );
			newHamburger.setAttribute( 'height', '18' );
			newHamburger.setAttribute( 'viewBox', '0 0 21 18' );
			newHamburger.setAttribute( 'fill', 'none' );
			newHamburger.setAttribute( 'xmlns', svgNsH );
			newHamburger.setAttribute( 'aria-hidden', 'true' );
			newHamburger.setAttribute( 'focusable', 'false' );
			var hPath = document.createElementNS( svgNsH, 'path' );
			hPath.setAttribute( 'd', 'M0 0.75C0 0.328125 0.328125 0 0.75 0H20.25C20.6719 0 21 0.328125 21 0.75C21 1.17188 20.6719 1.5 20.25 1.5H0.75C0.328125 1.5 0 1.17188 0 0.75ZM0 9C0 8.57812 0.328125 8.25 0.75 8.25H20.25C20.6719 8.25 21 8.57812 21 9C21 9.42188 20.6719 9.75 20.25 9.75H0.75C0.328125 9.75 0 9.42188 0 9ZM21 17.25C21 17.6719 20.6719 18 20.25 18H0.75C0.328125 18 0 17.6719 0 17.25C0 16.8281 0.328125 16.5 0.75 16.5H20.25C20.6719 16.5 21 16.8281 21 17.25Z' );
			hPath.setAttribute( 'fill', 'currentColor' );
			newHamburger.appendChild( hPath );
			oldHamburger.replaceWith( newHamburger );
		}

		// Build close (X) button via DOM — sits next to the hamburger.
		var closeBtn = document.createElement( 'button' );
		closeBtn.className = 'dclt-mobile-close-btn';
		closeBtn.setAttribute( 'type', 'button' );
		closeBtn.setAttribute( 'aria-label', 'Close menu' );
		closeBtn.style.display = 'none'; // Hidden by default.

		var svgNsNav = 'http://www.w3.org/2000/svg';
		var closeSvg = document.createElementNS( svgNsNav, 'svg' );
		closeSvg.setAttribute( 'xmlns', svgNsNav );
		closeSvg.setAttribute( 'viewBox', '0 0 24 24' );
		closeSvg.setAttribute( 'width', '24' );
		closeSvg.setAttribute( 'height', '24' );
		closeSvg.setAttribute( 'aria-hidden', 'true' );
		closeSvg.setAttribute( 'focusable', 'false' );
		closeSvg.setAttribute( 'fill', 'currentColor' );
		var closePath = document.createElementNS( svgNsNav, 'path' );
		closePath.setAttribute( 'd', 'm13.06 12 6.47-6.47-1.06-1.06L12 10.94 5.53 4.47 4.47 5.53 10.94 12l-6.47 6.47 1.06 1.06L12 13.06l6.47 6.47 1.06-1.06L13.06 12Z' );
		closeSvg.appendChild( closePath );
		closeBtn.appendChild( closeSvg );

		// Insert close button after the hamburger's parent nav element.
		openBtn.parentNode.insertBefore( closeBtn, openBtn.nextSibling );

		// Close button clicks the hidden WP close button.
		closeBtn.addEventListener( 'click', function ( e ) {
			e.preventDefault();
			e.stopPropagation();
			if ( wpCloseBtn ) wpCloseBtn.click();
		} );

		// Watch for is-menu-open class changes on the overlay.
		var navObserver = new MutationObserver( function ( mutations ) {
			mutations.forEach( function ( mutation ) {
				if ( mutation.attributeName !== 'class' ) return;
				if ( navOverlay.classList.contains( 'is-menu-open' ) ) {
					openBtn.style.display = 'none';
					closeBtn.style.display = 'flex';
					if ( header ) header.classList.add( 'dclt-site-header--menu-open' );
				} else {
					openBtn.style.display = '';
					closeBtn.style.display = 'none';
					if ( header ) header.classList.remove( 'dclt-site-header--menu-open' );
				}
			} );
		} );

		navObserver.observe( navOverlay, { attributes: true, attributeFilter: [ 'class' ] } );
	}

	// ═══════════════════════════════════════════════════════
	// Homepage Hero Scroll-Down Button
	// ═══════════════════════════════════════════════════════

	var scrollDownBtn = document.querySelector( '[data-scroll-down]' );
	if ( scrollDownBtn ) {
		scrollDownBtn.addEventListener( 'click', function () {
			// Find the hero wrapper and scroll to the next sibling section.
			var hero = scrollDownBtn.closest( '.dclt-homepage-hero' );
			if ( ! hero ) return;

			// The hero block's outermost wrapper may be nested inside
		// an extra div added by the block renderer. Walk up until
		// we find a sibling, checking each ancestor level.
			var wrapper = hero;
			var nextSection = null;

			while ( wrapper && ! nextSection ) {
				nextSection = wrapper.nextElementSibling;
				wrapper = wrapper.parentElement;
			}

			if ( nextSection ) {
				var headerOffset = header ? header.offsetHeight : 0;
				var top = nextSection.getBoundingClientRect().top + window.scrollY - headerOffset;

				window.scrollTo( {
					top: top,
					behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
				} );
			}
		} );
	}

	// ═══════════════════════════════════════════════════════
	// Hero Parallax
	// ═══════════════════════════════════════════════════════

	var heroBlock = document.querySelector( '.dclt-homepage-hero' );
	var heroImage = heroBlock ? heroBlock.querySelector( '.dclt-homepage-hero__image' ) : null;

	if ( heroImage && ! prefersReducedMotion.matches ) {
		var parallaxRate = 0.45;
		var parallaxTicking = false;

		function updateParallax() {
			var scrollY = window.scrollY;
			var heroBottom = heroBlock.offsetTop + heroBlock.offsetHeight;

			// Only apply while the hero is in view.
			if ( scrollY < heroBottom ) {
				heroImage.style.transform = 'translateY(' + ( scrollY * parallaxRate ) + 'px)';
			}
			parallaxTicking = false;
		}

		window.addEventListener( 'scroll', function () {
			if ( ! parallaxTicking ) {
				window.requestAnimationFrame( updateParallax );
				parallaxTicking = true;
			}
		}, { passive: true } );
	}

	// ═══════════════════════════════════════════════════════
	// Interior Hero Parallax
	// ═══════════════════════════════════════════════════════

	var interiorHero = document.querySelector( '.dclt-interior-hero' );
	var interiorImage = interiorHero ? interiorHero.querySelector( '.dclt-interior-hero__image' ) : null;

	if ( interiorImage && ! prefersReducedMotion.matches ) {
		var interiorParallaxRate = 0.35;
		var interiorTicking = false;

		function updateInteriorParallax() {
			var scrollY = window.scrollY;
			var heroBottom = interiorHero.offsetTop + interiorHero.offsetHeight;

			if ( scrollY < heroBottom ) {
				interiorImage.style.transform = 'translateY(' + ( scrollY * interiorParallaxRate ) + 'px)';
			}
			interiorTicking = false;
		}

		window.addEventListener( 'scroll', function () {
			if ( ! interiorTicking ) {
				window.requestAnimationFrame( updateInteriorParallax );
				interiorTicking = true;
			}
		}, { passive: true } );
	}

	// ═══════════════════════════════════════════════════════
	// CTA Banner & Cover Block Parallax
	// ═══════════════════════════════════════════════════════

	// Build a list of { container, bg } pairs for all parallax-eligible blocks.
	var parallaxItems = [];

	document.querySelectorAll( '.dclt-cta-banner' ).forEach( function ( el ) {
		var bg = el.querySelector( '.dclt-cta-banner__bg' );
		if ( bg ) parallaxItems.push( { container: el, bg: bg } );
	} );

	document.querySelectorAll( '.wp-block-cover' ).forEach( function ( el ) {
		var img = el.querySelector( '.wp-block-cover__image-background' );
		if ( img ) {
			el.classList.add( 'dclt-parallax-cover' );
			// Translate the <picture> wrapper if present, otherwise the <img> directly.
			var bg = img.closest( 'picture' ) || img;
			parallaxItems.push( { container: el, bg: bg } );
		}
	} );

	if ( parallaxItems.length && ! prefersReducedMotion.matches ) {
		var sectionParallaxRate = 0.3;
		var sectionTicking = false;

		function updateSectionParallax() {
			var viewportHeight = window.innerHeight;

			parallaxItems.forEach( function ( item ) {
				var rect = item.container.getBoundingClientRect();

				// Only process when in or near the viewport.
				if ( rect.bottom < 0 || rect.top > viewportHeight ) return;

				var center = rect.top + rect.height / 2;
				var viewportCenter = viewportHeight / 2;
				var offset = ( center - viewportCenter ) * sectionParallaxRate;

				item.bg.style.transform = 'translateY(' + offset + 'px)';
			} );

			sectionTicking = false;
		}

		window.addEventListener( 'scroll', function () {
			if ( ! sectionTicking ) {
				window.requestAnimationFrame( updateSectionParallax );
				sectionTicking = true;
			}
		}, { passive: true } );

		// Run once on load to set initial position.
		updateSectionParallax();
	}

	// ═══════════════════════════════════════════════════════
	// Anchor Nav Scroll-Spy & Mobile Dropdown
	// ═══════════════════════════════════════════════════════

	var anchorNav = document.querySelector( '.dclt-anchor-nav' );

	if ( anchorNav ) {
		var anchorLinks   = anchorNav.querySelectorAll( '.dclt-anchor-nav__link' );
		var anchorToggle  = anchorNav.querySelector( '.dclt-anchor-nav__toggle' );
		var toggleLabel   = anchorNav.querySelector( '.dclt-anchor-nav__toggle-label' );
		var sectionMap    = [];

		// Mobile dropdown toggle.
		if ( anchorToggle ) {
			anchorToggle.addEventListener( 'click', function () {
				var isOpen = anchorNav.classList.toggle( 'dclt-anchor-nav--open' );
				anchorToggle.setAttribute( 'aria-expanded', isOpen ? 'true' : 'false' );
			} );
		}

		// Close dropdown when a link is clicked (mobile).
		function closeMobileDropdown() {
			if ( anchorNav.classList.contains( 'dclt-anchor-nav--open' ) ) {
				anchorNav.classList.remove( 'dclt-anchor-nav--open' );
				if ( anchorToggle ) anchorToggle.setAttribute( 'aria-expanded', 'false' );
			}
		}

		// Update the toggle label to match the active section.
		function updateToggleLabel( text ) {
			if ( toggleLabel ) {
				toggleLabel.textContent = text;
			}
		}

		anchorLinks.forEach( function ( link ) {
			var id = link.getAttribute( 'href' );
			if ( ! id || id.charAt( 0 ) !== '#' ) return;
			var target = document.querySelector( id );
			if ( target ) {
				sectionMap.push( { link: link, target: target } );
			}
		} );

		if ( sectionMap.length ) {
			// IntersectionObserver: detect which section is in view.
			// Negative top margin accounts for sticky header + anchor nav height.
			var observer = new IntersectionObserver( function ( entries ) {
				entries.forEach( function ( entry ) {
					if ( ! entry.isIntersecting ) return;

					anchorLinks.forEach( function ( l ) {
						l.classList.remove( 'is-active' );
					} );

					for ( var i = 0; i < sectionMap.length; i++ ) {
						if ( sectionMap[ i ].target === entry.target ) {
							sectionMap[ i ].link.classList.add( 'is-active' );
							updateToggleLabel( sectionMap[ i ].link.textContent.trim() );
							break;
						}
					}
				} );
			}, {
				rootMargin: '-200px 0px -60% 0px',
				threshold: 0,
			} );

			sectionMap.forEach( function ( item ) {
				observer.observe( item.target );
			} );

			// Enhanced smooth-scroll with offset for sticky elements.
			anchorLinks.forEach( function ( link ) {
				link.addEventListener( 'click', function ( e ) {
					var id = link.getAttribute( 'href' );
					var target = document.querySelector( id );
					if ( ! target ) return;

					e.preventDefault();
					closeMobileDropdown();

					var headerH    = header ? header.offsetHeight : 0;
					var anchorNavH = anchorNav.offsetHeight || 0;
					var offset     = headerH + anchorNavH;
					var top        = target.getBoundingClientRect().top + window.scrollY - offset;

					window.scrollTo( {
						top: top,
						behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
					} );

					// Update URL hash without jumping.
					history.pushState( null, '', id );
				} );
			} );
		}

		// Close dropdown when clicking outside.
		document.addEventListener( 'click', function ( e ) {
			if ( ! anchorNav.contains( e.target ) ) {
				closeMobileDropdown();
			}
		} );
	}

} )();
